import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Search, RefreshCw, Filter, Eye, MessageSquare, Globe,
  Monitor, Smartphone, Clock, MapPin, ArrowLeft, Activity,
  ExternalLink, ChevronRight, User, Mail, Phone
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LiveChatVisitors = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [websites, setWebsites] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [websiteFilter, setWebsiteFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [identifiedFilter, setIdentifiedFilter] = useState('all');
  
  // Stats
  const [activeCount, setActiveCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchVisitors();
    fetchWebsites();
    
    // Poll for updates every 15 seconds
    const interval = setInterval(fetchVisitors, 15000);
    return () => clearInterval(interval);
  }, [token, page, websiteFilter, statusFilter, identifiedFilter]);

  const fetchVisitors = async () => {
    try {
      const params = new URLSearchParams({
        token,
        page: page.toString(),
        limit: '50'
      });
      
      if (websiteFilter !== 'all') params.append('website_id', websiteFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (identifiedFilter === 'identified') params.append('is_identified', 'true');
      if (identifiedFilter === 'anonymous') params.append('is_identified', 'false');
      
      const response = await axios.get(`${API}/api/live-chat/visitors?${params}`);
      
      if (response.data.success) {
        setVisitors(response.data.visitors);
        setActiveCount(response.data.active_count || 0);
        setTotalCount(response.data.total || 0);
        setTotalPages(response.data.pages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch visitors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWebsites = async () => {
    try {
      const response = await axios.get(`${API}/api/live-chat/websites?token=${token}&status=active`);
      if (response.data.success) {
        setWebsites(response.data.websites);
      }
    } catch (error) {
      console.error('Failed to fetch websites:', error);
    }
  };

  const filteredVisitors = visitors.filter(v => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (v.visitor_name || '').toLowerCase().includes(query) ||
      (v.visitor_email || '').toLowerCase().includes(query) ||
      (v.current_page || '').toLowerCase().includes(query)
    );
  });

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    
    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    // Format in UAE timezone (Asia/Dubai - UTC+4)
    return date.toLocaleDateString('en-AE', { timeZone: 'Asia/Dubai' });
  };
  
  // Format time in UAE timezone
  const formatTimeUAE = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-AE', { 
      timeZone: 'Asia/Dubai',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDeviceIcon = (deviceType) => {
    if (deviceType === 'mobile') return <Smartphone size={14} />;
    return <Monitor size={14} />;
  };

  const startChat = (visitor) => {
    // Navigate to chat creation with visitor context
    navigate(`/marketing/live-chat/chats/new?visitor_id=${visitor.visitor_id}`);
  };

  return (
    <div className="space-y-6" data-testid="live-chat-visitors">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/marketing/live-chat')}>
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Live Visitors</h1>
            <p className="text-sm text-slate-500">Monitor visitors on your websites in real-time</p>
          </div>
        </div>
        <Button onClick={fetchVisitors} variant="outline" className="gap-2">
          <RefreshCw size={14} /> Refresh
        </Button>
      </div>

      {/* Stats Bar */}
      <div className="flex items-center gap-6 p-4 bg-slate-50 rounded-lg">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-sm font-medium text-slate-700">{activeCount} Active Now</span>
        </div>
        <div className="text-sm text-slate-500">
          {totalCount} total visitors
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search visitors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="visitor-search"
          />
        </div>
        
        <Select value={websiteFilter} onValueChange={setWebsiteFilter}>
          <SelectTrigger className="w-[160px] bg-white">
            <SelectValue placeholder="All Websites" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Websites</SelectItem>
            {websites.map(w => (
              <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px] bg-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="idle">Idle</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={identifiedFilter} onValueChange={setIdentifiedFilter}>
          <SelectTrigger className="w-[150px] bg-white">
            <SelectValue placeholder="All Visitors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Visitors</SelectItem>
            <SelectItem value="identified">Identified</SelectItem>
            <SelectItem value="anonymous">Anonymous</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Visitors List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : filteredVisitors.length === 0 ? (
        <Card className="p-12 text-center">
          <Users size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No visitors found</h3>
          <p className="text-slate-500">
            {websiteFilter !== 'all' || statusFilter !== 'all' 
              ? 'Try adjusting your filters'
              : 'Visitors will appear here once they visit your tracked websites'}
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden border-slate-200">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-[50px]">Status</TableHead>
                <TableHead>Visitor</TableHead>
                <TableHead>Current Page</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Device</TableHead>
                <TableHead>Pages</TableHead>
                <TableHead>Time on Site</TableHead>
                <TableHead>Last Activity</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredVisitors.map(visitor => (
                <TableRow 
                  key={visitor.visitor_id} 
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => navigate(`/marketing/live-chat/visitors/${visitor.visitor_id}`)}
                  data-testid={`visitor-row-${visitor.visitor_id}`}
                >
                  <TableCell>
                    <div className={`w-2.5 h-2.5 rounded-full ${
                      visitor.is_online ? 'bg-emerald-500' : 'bg-slate-300'
                    }`} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className="bg-slate-200 text-slate-600 text-xs">
                          {visitor.is_identified 
                            ? (visitor.visitor_name || visitor.visitor_email || 'V')[0].toUpperCase()
                            : '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {visitor.visitor_name || visitor.visitor_email || 'Anonymous Visitor'}
                        </p>
                        {visitor.is_returning && (
                          <Badge variant="outline" className="text-xs mt-0.5">Returning</Badge>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 max-w-[200px]">
                      <Globe size={12} className="text-slate-400 flex-shrink-0" />
                      <span className="text-sm text-slate-600 truncate" title={visitor.current_page}>
                        {visitor.current_page?.replace(/^https?:\/\/[^/]+/, '') || '/'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-slate-600">
                      {visitor.referrer ? new URL(visitor.referrer).hostname : 'Direct'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 text-slate-600">
                      {getDeviceIcon(visitor.device_type)}
                      <span className="text-sm">{visitor.browser || 'Unknown'}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-slate-600">{visitor.page_views || 0}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-slate-600">
                      {formatDuration(visitor.total_time_seconds)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-slate-500">
                      {formatTimeAgo(visitor.last_seen_at)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate(`/marketing/live-chat/visitors/${visitor.visitor_id}`)}
                      >
                        <Eye size={14} />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => startChat(visitor)}
                        className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                      >
                        <MessageSquare size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-slate-500">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default LiveChatVisitors;
